import Axios from 'axios';
import { action, observable, computed } from 'mobx';
import { Notification } from 'element-react';
import { API } from '@/config/api';
import i18n from '@/i18n';
import { mySetInterval } from '@/utils/fn';
import { TpslConditionType } from '@/pages/futures/main/orderForm/tpslCondition';

export interface PositionItem {
  symbolName: string; // 交易对名称
  autoMargin: boolean; // 是否自动最佳保证金
  availableCloseSize: string; // 可平仓数量（张）
  closeOrderSize: number; // 平仓挂单数量
  entryPrice: string; // 持仓成本价
  isolatedMargin: string; // 逐仓保证金
  leverage: number; // 杠杆倍数
  openOrderMarginFrozen: string; // 开仓订单冻结保证金
  positionSide: PositionSide; // 持仓方向
  positionSize: string; // 持仓数量
  positionType: PositionType; // 仓位类型
  positionModel: PositionMode; // 仓位模式
  realizedProfit: string; // 已实现盈亏
  state: number; // 仓位状态 0：无；1：使用中；2：强平中；3：冻结
  symbol: string; // 交易对
  unsettledProfit: string; // 开仓手续费
  positionId: string; // 仓位ID
  isLeaderPosition?: boolean; // 是否为带单仓位（交易员下单）
  balanceType?: string; // 仓位类型：COPY-跟单仓位
  /** Step28：NORMAL / POSITION_EXPERIENCE；组合键需 source+id */
  source?: 'NORMAL' | 'POSITION_EXPERIENCE' | string;
  isExperiencePosition?: boolean;
  experiencePosition?: boolean;
  canOperate?: boolean;
  priceStale?: boolean | null;
  experienceStatus?: string;
  userCouponId?: number;
  expireTime?: number;
  markPrice?: string;
  profitId?: string;
  triggerProfitPrice?: string;
  triggerProfitConditionType?: TpslConditionType;
  triggerProfitConditionValue?: string;
  triggerStopPrice?: string;
  triggerStopConditionType?: TpslConditionType;
  triggerStopConditionValue?: string;
  hasAllPositionProfit?: boolean;

  // 本地计算
  floatProfit?: string; // 浮动盈亏
  floatProfitRate?: string; // 收益率
  adl?: number; // adl
  initialMargin: string; // 起始保证金
  liquidationPrice?: string; // 预估强平价
  maintMargin: string; // 维持保证金

  // 其他配置
  contractSize: number; // 面值
  quantityPrecision?: number; // 数量精度
  pricePrecision?: number; // 价格精度
  quoteCoinDisplayPrecision?: number; // 计价币种显示精度
}

export interface AdlItem {
  positionId: string;
  longQuantile: number;
  shortQuantile: number;
  symbol: string;
}

/** 体验仓 WebSocket 事件（channel: user.experience.position） */
export interface ExperiencePositionEvent {
  eventId?: string;
  eventType?: 'OPENED' | 'CLOSED' | string;
  source?: string;
  positionId?: string;
  version?: number;
  status?: string;
  closeReason?: string;
  closeTime?: number;
  [key: string]: any;
}

const EXPERIENCE_EVENT_ID_CACHE_SIZE = 500;

export default class Position {
  @observable positionList: PositionItem[];

  @observable allPositionList: PositionItem[];

  @observable adlList: AdlItem[];

  /** 体验仓当前持仓：独立接口 + user.experience.position 推送共同维护 */
  @observable experiencePositionList: PositionItem[];

  /** 已处理的 eventId，Kafka 至少一次投递会重复推送 */
  private experienceEventIds = new Set<string>();

  private experienceEventIdQueue: string[] = [];

  /** source+positionId → 已接受的最大 version */
  private experienceVersions = new Map<string, number>();

  /** 关闭墓碑：阻止迟到的旧 OPENED 复活已平掉的仓位 */
  private experienceClosedKeys = new Map<string, number>();

  constructor() {
    this.positionList = [];
    this.allPositionList = [];
    this.adlList = [];
    this.experiencePositionList = [];
  }

  @action
  setPositionList(data: PositionItem[]) {
    this.positionList = data;
  }

  @action.bound
  async fetchPositionList(params?: { symbol?: string; source?: string }, retried = false) {
    try {
      const result: any = await Axios.get(API.POSITION_LIST, {
        params: {
          symbol: params?.symbol,
          // Step28：ALL / POSITION_EXPERIENCE 走聚合；不传保持旧 NORMAL 行为
          ...(params?.source ? { source: params.source } : {}),
        },
      });

      const list = Array.isArray(result) ? result : Array.isArray(result?.items) ? result.items : null;
      if (list) {
        const { normalizeContractPositionList } = await import('@/pages/welfareCenter/experienceQuery');
        this.setPositionList(normalizeContractPositionList(list));
      }
    } catch {
      // source 聚合参数后端未就绪时回退普通列表，避免 Unhandled Promise Rejection
      if (params?.source && !retried) {
        await this.fetchPositionList({ symbol: params?.symbol }, true);
      }
    }
  }

  @action
  setAllPositionList(data: PositionItem[]) {
    this.allPositionList = data;
  }

  /** 仓位唯一键必须是 source+positionId：真实仓与体验仓可能同交易对同方向 */
  private experienceKey(positionId?: string | number) {
    return `POSITION_EXPERIENCE:${String(positionId ?? '')}`;
  }

  private markExperienceEventHandled(eventId?: string) {
    if (!eventId) return false;
    if (this.experienceEventIds.has(eventId)) return true;
    this.experienceEventIds.add(eventId);
    this.experienceEventIdQueue.push(eventId);
    if (this.experienceEventIdQueue.length > EXPERIENCE_EVENT_ID_CACHE_SIZE) {
      const expired = this.experienceEventIdQueue.shift();
      if (expired) this.experienceEventIds.delete(expired);
    }
    return false;
  }

  @action.bound
  setExperiencePositionList(data: PositionItem[]) {
    // 过滤并发返回的旧请求结果：已收到 CLOSED 的仓位不再回显
    this.experiencePositionList = (data || []).filter(
      item => !this.experienceClosedKeys.has(this.experienceKey(item.positionId)),
    );
  }

  /**
   * 撤销体验仓止盈止损后立刻清掉持仓展示字段。
   * 委托列表刷新不会改 experiencePositionList，且 inheritExperienceTpsl 可能从聚合列表回填旧价。
   */
  @action.bound
  clearExperiencePositionTpsl(
    positionId?: string | number,
    sides?: { profit?: boolean; stop?: boolean },
  ) {
    const clearProfit = sides?.profit !== false;
    const clearStop = sides?.stop !== false;
    const targetKey = positionId == null ? null : this.experienceKey(positionId);

    const patch = (row: PositionItem): PositionItem => {
      if (targetKey != null && this.experienceKey(row.positionId) !== targetKey) return row;
      const next: PositionItem = { ...row };
      if (clearProfit) {
        next.triggerProfitPrice = undefined;
        next.triggerProfitConditionType = undefined;
        next.triggerProfitConditionValue = undefined;
        (next as any).takeProfitPrice = undefined;
        (next as any).takeProfitEntrustId = undefined;
      }
      if (clearStop) {
        next.triggerStopPrice = undefined;
        next.triggerStopConditionType = undefined;
        next.triggerStopConditionValue = undefined;
        (next as any).stopLossPrice = undefined;
        (next as any).stopLossEntrustId = undefined;
      }
      if (clearProfit && clearStop) {
        next.profitId = undefined;
        (next as any).entrusts = [];
        (next as any).profitList = [];
      }
      return next;
    };

    this.experiencePositionList = this.experiencePositionList.map(patch);
    this.positionList = this.positionList.map(row => {
      const isExp =
        row.source === 'POSITION_EXPERIENCE' ||
        row.isExperiencePosition ||
        row.experiencePosition ||
        (row as any).balanceType === 'EXPERIENCE';
      return isExp ? patch(row) : row;
    });
  }

  /** 重连、listenKey 重建、页面从后台恢复后整体替换体验仓快照 */
  @action.bound
  async fetchExperiencePositionList(symbolId?: number) {
    try {
      const { fetchExperiencePositionCurrent } = await import('@/pages/welfareCenter/experienceQuery');
      const list = await fetchExperiencePositionCurrent(symbolId);
      this.setExperiencePositionList(list);
      // 快照已按墓碑过滤，重拉后即可释放；迟到的旧事件仍由 version 比较拦截
      this.experienceClosedKeys.clear();
    } catch {
      // 未登录或接口不可用时保持现有快照
    }
  }

  /** 开仓接口成功后本地即时补位，不等 WebSocket 往返 */
  @action.bound
  upsertExperiencePosition(item: PositionItem) {
    const key = this.experienceKey(item.positionId);
    if (this.experienceClosedKeys.has(key)) return;

    const index = this.experiencePositionList.findIndex(
      row => this.experienceKey(row.positionId) === key,
    );
    if (index > -1) {
      this.experiencePositionList = this.experiencePositionList.map((row, i) => (i === index ? item : row));
    } else {
      this.experiencePositionList = [item, ...this.experiencePositionList];
    }
  }

  /** 平仓接口成功后本地即时移除，并留下墓碑 */
  @action.bound
  removeExperiencePosition(positionId?: string | number, version?: number) {
    const key = this.experienceKey(positionId);
    this.experiencePositionList = this.experiencePositionList.filter(
      row => this.experienceKey(row.positionId) !== key,
    );
    this.experienceClosedKeys.set(key, Number(version ?? Number.MAX_SAFE_INTEGER));
  }

  /**
   * user.experience.position 推送处理
   * OPENED 是完整快照，必须整体覆盖（含用 null 清空止盈止损），不能只合并非空字段
   */
  @action.bound
  async applyExperiencePositionEvent(data: ExperiencePositionEvent) {
    if (!data || data.source !== 'POSITION_EXPERIENCE' || !data.positionId) return;

    const key = this.experienceKey(data.positionId);
    if (this.markExperienceEventHandled(data.eventId)) return;

    const version = Number(data.version ?? 0);
    const lastVersion = this.experienceVersions.get(key);
    if (lastVersion !== undefined && version < lastVersion) return;
    this.experienceVersions.set(key, version);

    if (data.eventType === 'CLOSED') {
      this.removeExperiencePosition(data.positionId, version);
      return;
    }

    if (data.eventType !== 'OPENED') return;

    const closedVersion = this.experienceClosedKeys.get(key);
    // 补偿重试可能让旧 OPENED 晚于 CLOSED 到达，墓碑必须阻止它复活仓位
    if (closedVersion !== undefined && version <= closedVersion) return;

    const { mapExperiencePositionToPositionItem, resolvePositionTpslPrices } = await import(
      '@/pages/welfareCenter/experienceQuery'
    );
    this.experienceClosedKeys.delete(key);
    const mapped = mapExperiencePositionToPositionItem(data);
    const current = resolvePositionTpslPrices(mapped);
    // OPENED 是完整快照：以推送解析结果为准（空值即清空），禁止回填旧止盈止损
    this.upsertExperiencePosition({
      ...mapped,
      triggerProfitPrice: current.triggerProfitPrice,
      triggerStopPrice: current.triggerStopPrice,
    });
  }

  // 一键平仓 当前持仓
  @action.bound
  async fetchPositionCloseAll(symbol?: string, positionSource: 'ALL' | 'SELF' | 'EXPERIENCE' = 'ALL') {
    const result: any = await Axios.post(API.POSITION_COLSE_ALL, {
      symbol,
      positionSource,
    }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || error || i18n.t('failure.position'),
        offset: 60,
        duration: 2000,
      });
    });

    if (result) {
      Notification.success({
        message: i18n.t('success.position'),
        offset: 60,
        duration: 2000,
      });
      return Promise.resolve(result);
    }
  }
  // 一键平仓 带单持仓
  @action.bound
  async fetchLeaderOrderCloseAll(symbol?: string) {
    const result: any = await Axios.post(API.LEADER_ORDER_CLOSE_ALL, { symbol }).catch(({ error }: any) => {
      Notification.error({
        message: error?.msg || error || i18n.t('failure.position'),
        offset: 60,
        duration: 2000,
      });

      if (result) {
        Notification.success({
          message: i18n.t('success.position'),
          offset: 60,
          duration: 2000,
        });
        return Promise.resolve(result);
      }
    })
  }
  @action.bound
  async fetchAllPositionList() {
    // 项目仅支持 U 本位
    const uBasedPositionList = await Axios.get(API.POSITION_LIST, {
      baseURL: '/f',
    });

    let result: any = [];

    if (Array.isArray(uBasedPositionList)) {
      result = result.concat(uBasedPositionList);
    }

    this.setAllPositionList(result);
  }

  @action.bound
  updatePositionListByRow(params: PositionItem) {
    const { positionId } = params;
    // current underlying position
    const index = this.positionList.findIndex(item => item.positionId === positionId);

    const newList = this.positionList;
    if (index > -1) {
      const target = newList[index];
      const newItem = {
        ...target,
        ...params,
      };
      newList.splice(index, 1, newItem);
    } else {
      newList.unshift(params);
    }

    this.setPositionList(newList);

    // all position
    const indexAll = this.allPositionList.findIndex(item => item.positionId === positionId);

    const newListAll = this.allPositionList;
    if (indexAll > -1) {
      const target = newListAll[indexAll];
      const newItem = {
        ...target,
        ...params,
      };
      newListAll.splice(indexAll, 1, newItem);
    } else {
      newListAll.unshift(params);
    }

    this.setAllPositionList(newListAll);
  }

  // 修改自动追加保证金
  @action.bound
  async fetchUpdateAutoMargin(params: {
    symbol: string;
    positionSide: string;
    autoMargin: boolean;
    positionId: string;
  }) {
    const { positionId, autoMargin } = params;

    try {
      return await Axios.post(API.POSITION_AUTO_MARGIN, params)
        .then(() => {
          const newList = [...this.positionList];
          const index = newList.findIndex((item: PositionItem) => positionId === item.positionId);

          const position = this.positionList[index];
          position.autoMargin = autoMargin;

          newList.splice(index, 1, position);
          this.setPositionList(newList);
        })
        .catch(({ error }: any) => {
          Notification.error({
            message: error?.msg,
            offset: 60,
            duration: 2000,
          });
        });
    } catch (e) {
      return Promise.reject(e);
    }
  }

  // 自动减仓等级
  @action
  setAdlList(data: AdlItem[]) {
    this.adlList = data;
  }

  @action.bound
  async fetchAdlList(params?: { symbol: string }) {
    const result: any = await Axios.get(API.POSITION_ADL_LIST, {
      params: {
        symbol: params?.symbol,
      },
    });

    if (Array.isArray(result)) {
      this.setAdlList(result);
    }
  }

  @computed get adlMap() {
    const positionList = this.positionList;
    const adlList = this.adlList;
    const result: { [key: string]: any } = {};

    for (let i = 0; i < positionList.length; i += 1) {
      const item = positionList[i];
      const { positionId } = item;

      const adl = adlList.find(o => o.positionId === positionId);

      if (adl) {
        result[positionId] = adl;
      }
    }

    return result;
  }
}
